import React, { useContext, useEffect, useState, useRef } from 'react';

import { PHRPagination, PHRSuccessCheckmark as SuccessCheckmark } from '@palmhr/palmhr-ui-lib';
import _ from 'lodash';
import Modal from 'react-modal';

import InvoicesTable from './InvoicesTable';
import CustomersApi from '../../../../api/CustomersApi';
import { AuthContext } from '../../../../Auth/AuthContext';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import modalStyles from '../../../../components/modalStyles';
import useActionState from '../../../../hooks/useActionState';
import ClientContext from '../ClientContext';

const addModalStyle = _.cloneDeep(modalStyles);
addModalStyle.content.width = '640px';
addModalStyle.content.marginTop = '100px';

const Invoices = () => {
  const { selected } = useContext(ClientContext);
  const { authState } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [invoicesPagination, setInvoicesPagination] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [retryNotification, setRetryNotification] = useState(null);
  const [hasCardPayment, setHasCardPayment] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const timeoutRef = useRef(null);

  const isAdmin = authState?.user?.roles?.includes('ROLE_ADMIN') || false;

  // Action states for different operations
  const downloadAction = useActionState();
  const deleteAction = useActionState();
  const markAsPaidAction = useActionState();

  const [params, setParams] = useState({
    currentPage: 1,
    perPage: 10,
  });

  const fetchInvoices = () => {
    if (selected) {
      setLoadingData(true);

      CustomersApi.getCustomerInvoices(selected.id, params)
        .then((res) => {
          setData(res.data.items || []);
          setInvoicesPagination(res.data.pagination || []);
          setLoadingData(false);
        })
        .catch(() => {
          setLoadingData(false);
        });
    }
  };

  useEffect(() => {
    if (selected) {
      // Use the hasPaymentConfigured flag from the backend
      setHasCardPayment(selected.hasPaymentConfigured || false);

      fetchInvoices();
    }
  }, [selected, params]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const updatePaginationParams = ({ currentPage, perPage }) =>
    setParams((prevState) => ({ ...prevState, currentPage, perPage }));

  const onDownload = async (invoice, action) => {
    const param = selected.id;

    await downloadAction.execute(CustomersApi.downloadInvoice(param, invoice, action), {
      successMessage: action === 'preview' ? 'Opening invoice preview...' : 'Downloading invoice...',
      errorMessage: `Failed to ${action} invoice`,
      onSuccess: (response) => {
        if (action === 'preview') {
          window.open(response.data?.download?.download_url);
        } else {
          const link = document.createElement('a');
          link.href = response.data?.download?.download_url;
          link.setAttribute('download', 'invoice.pdf');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      },
    });
  };

  const onDelete = async () => {
    setIsSending(true);
    const param = selected.id;

    await deleteAction.execute(CustomersApi.deleteInvoice(param, selectedId), {
      successMessage: 'Invoice deleted successfully',
      errorMessage: 'Failed to delete invoice',
      onSuccess: () => {
        let state = [...data];
        state = state.filter((el) => el.id !== selectedId);
        setData(state);
        setSent(true);
        setTimeout(() => {
          setSent(false);
          setConfirmModal(false);
        }, 1000);
      },
    });
    setIsSending(false);
  };

  const onMarkAsPaid = async () => {
    setIsSending(true);
    const param = selected.id;

    await markAsPaidAction.execute(CustomersApi.markAsPaid(param, selectedId), {
      successMessage: 'Invoice marked as paid successfully',
      errorMessage: 'Failed to mark invoice as paid',
      onSuccess: () => {
        const state = [...data];
        const index = _.findIndex(state, ['id', selectedId]);
        state[index].status = 'paid';
        setData(state);
        setSent(true);
        setTimeout(() => {
          setSent(false);
          setConfirmModal(false);
        }, 1000);
      },
    });
    setIsSending(false);
  };

  const actionHandler = (action, id) => {
    setActionType(action);
    setSelectedId(id);
    setConfirmModal(true);
  };

  const onRetryPayment = (invoiceId) => {
    if (isRetrying) return;

    setIsRetrying(true);
    setRetryNotification({ show: true, loading: true });

    CustomersApi.retryPayment(invoiceId)
      .then(() => {
        setRetryNotification({
          show: true,
          loading: false,
          success: true,
          message: 'Payment retry initiated successfully',
        });
        // Refresh invoice data
        fetchInvoices();
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setRetryNotification(null);
        }, 3000);
      })
      .catch((error) => {
        setRetryNotification({
          show: true,
          loading: false,
          success: false,
          message: error.response?.data?.message || 'Failed to retry payment',
        });
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setRetryNotification(null);
        }, 5000);
      })
      .finally(() => {
        setIsRetrying(false);
      });
  };

  return (
    <div className='h-100 d-flex justify-content-start flex-column gap-2 me-5' style={{ overflowY: 'hidden' }}>
      <div className='mt-3' style={{ height: 'calc(100vh - 265px)' }}>
        <InvoicesTable
          isLoading={loadingData}
          data={data}
          actionHandler={actionHandler}
          handleDownload={onDownload}
          onRetryPayment={onRetryPayment}
          hasCardPayment={hasCardPayment}
          isAdmin={isAdmin}
          downloadAction={downloadAction}
          deleteAction={deleteAction}
          markAsPaidAction={markAsPaidAction}
          selectedId={selectedId}
          isRetrying={isRetrying}
        />
      </div>
      {invoicesPagination?.total > 0 && (
        <PHRPagination onPaginationAction={updatePaginationParams} pagination={invoicesPagination} />
      )}

      <Modal
        isOpen={confirmModal}
        contentLabel='Add Client'
        ariaHideApp={false}
        closeTimeoutMS={300}
        style={addModalStyle}
        onRequestClose={() => {
          setConfirmModal(false);
        }}
      >
        <ConfirmModal
          isSending={isSending}
          sent={sent}
          onClose={() => {
            setConfirmModal(false);
          }}
          onSubmit={actionType === 'paid' ? onMarkAsPaid : onDelete}
          modalName={actionType === 'paid' ? 'Mark invoice paid' : 'Delete Invoice'}
          confirmName={actionType === 'paid' ? 'Confirm' : 'Delete'}
          confirmText={
            actionType === 'paid'
              ? 'Are you sure you want to mark this as paid?'
              : 'Are you sure you want to delete this invoice?'
          }
        />
      </Modal>
      {retryNotification?.show && (
        <SuccessCheckmark
          positionFixed
          text={retryNotification.loading ? 'Processing payment retry...' : retryNotification.message}
          isFailed={!retryNotification.success && !retryNotification.loading}
          hideCloseButton={retryNotification.loading}
        />
      )}
      {downloadAction.notification?.show && (
        <SuccessCheckmark
          positionFixed
          text={downloadAction.notification.loading ? 'Processing...' : downloadAction.notification.message}
          isFailed={!downloadAction.notification.success && !downloadAction.notification.loading}
          hideCloseButton={downloadAction.notification.loading}
        />
      )}
      {deleteAction.notification?.show && (
        <SuccessCheckmark
          positionFixed
          text={deleteAction.notification.loading ? 'Deleting invoice...' : deleteAction.notification.message}
          isFailed={!deleteAction.notification.success && !deleteAction.notification.loading}
          hideCloseButton={deleteAction.notification.loading}
        />
      )}
      {markAsPaidAction.notification?.show && (
        <SuccessCheckmark
          positionFixed
          text={markAsPaidAction.notification.loading ? 'Marking as paid...' : markAsPaidAction.notification.message}
          isFailed={!markAsPaidAction.notification.success && !markAsPaidAction.notification.loading}
          hideCloseButton={markAsPaidAction.notification.loading}
        />
      )}
    </div>
  );
};

export default Invoices;
